import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertCircle, Eye } from 'lucide-react';
import { webhookService, WebhookLog, WebhookLogFilters } from '@/services/webhookService';
import SmartPagination from '@/components/ui/smart-pagination';
import WebhookLogDetailSheet from './WebhookLogDetailSheet';

interface WebhookLogsTabProps {
  clinicId: string;
  webhookId: string;
}

const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return 'Invalid date';
  }
};

const formatRelativeTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  } catch {
    return 'Invalid date';
  }
};

const WebhookLogsTab: React.FC<WebhookLogsTabProps> = ({ clinicId, webhookId }) => {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
  });
  const [filters, setFilters] = useState<WebhookLogFilters>({
    page: 1,
    limit: 20,
    success: 'all',
    event_type: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    fetchLogs();
  }, [clinicId, webhookId, filters.page, filters.limit, filters.success, filters.event_type, filters.start_date, filters.end_date]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params: WebhookLogFilters = {
        page: filters.page || 1,
        limit: filters.limit || 20,
      };
      
      if (filters.success && filters.success !== 'all') {
        params.success = filters.success === 'true';
      }
      if (filters.event_type) {
        params.event_type = filters.event_type;
      }
      if (filters.start_date) {
        params.start_date = filters.start_date;
      }
      if (filters.end_date) {
        params.end_date = filters.end_date;
      }

      const response = await webhookService.getWebhookLogs(clinicId, webhookId, params);
      setLogs(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to fetch webhook logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
  };

  const handleFilterChange = (key: keyof WebhookLogFilters, value: string | number) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleViewLog = (log: WebhookLog) => {
    setSelectedLog(log);
    setIsDetailSheetOpen(true);
  };

  const handleCloseDetailSheet = () => {
    setIsDetailSheetOpen(false);
    setSelectedLog(null);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Status</label>
          <Select
            value={filters.success?.toString() || 'all'}
            onValueChange={(value) => handleFilterChange('success', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="true">Success</SelectItem>
              <SelectItem value="false">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Event Type</label>
          <Input
            placeholder="Filter by event type"
            value={filters.event_type || ''}
            onChange={(e) => handleFilterChange('event_type', e.target.value)}
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Start Date</label>
          <Input
            type="datetime-local"
            value={filters.start_date || ''}
            onChange={(e) => handleFilterChange('start_date', e.target.value)}
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm font-medium text-slate-700 mb-1 block">End Date</label>
          <Input
            type="datetime-local"
            value={filters.end_date || ''}
            onChange={(e) => handleFilterChange('end_date', e.target.value)}
          />
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="h-12 w-12 text-slate-400 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No logs found</h3>
          <p className="text-sm text-slate-600 max-w-md">
            No webhook delivery logs match your current filters.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Event Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Response Code</TableHead>
                  <TableHead>Response Time</TableHead>
                  <TableHead>Attempt</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow
                    key={log.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleViewLog(log)}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm text-slate-900">
                          {formatRelativeTime(log.created_at)}
                        </span>
                        <span className="text-xs text-slate-500">
                          {formatDate(log.created_at)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-900 font-mono">
                        {log.event_type}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={log.success ? 'default' : 'destructive'}
                        className={
                          log.success
                            ? 'bg-green-100 text-green-800 border-green-200'
                            : 'bg-red-100 text-red-800 border-red-200'
                        }
                      >
                        {log.success ? 'Success' : 'Failed'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-900">
                        {log.response_status_code || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-900">
                        {log.response_time_ms ? `${log.response_time_ms}ms` : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-900">
                        {log.attempt_number}
                        {log.retry_of_log_id && (
                          <span className="text-xs text-slate-500 ml-1">(Retry)</span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewLog(log);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex flex-col items-center gap-3 border-t border-slate-200 px-4 py-3 sm:flex-row sm:justify-between">
              <p className="whitespace-nowrap text-xs text-slate-500">
                Showing {pagination.page * pagination.limit - pagination.limit + 1} -{' '}
                {pagination.page * pagination.limit > pagination.total
                  ? pagination.total
                  : pagination.page * pagination.limit}{' '}
                of {pagination.total} logs
              </p>
              <SmartPagination
                currentPage={pagination.page}
                onPageChange={handlePageChange}
                totalPages={pagination.total_pages}
                maxVisiblePages={5}
                className="mx-0 justify-center sm:justify-end"
              />
            </div>
          )}
        </>
      )}

      {/* Log Detail Sheet */}
      {selectedLog && (
        <WebhookLogDetailSheet
          open={isDetailSheetOpen}
          onOpenChange={setIsDetailSheetOpen}
          clinicId={clinicId}
          webhookId={webhookId}
          logId={selectedLog.id}
          onClose={handleCloseDetailSheet}
        />
      )}
    </div>
  );
};

export default WebhookLogsTab;

